import { useEffect, useState } from "react";
import { COMMUNITY_STATS } from "@/data/content";
import { useInView } from "@/hooks/useInView";

/**
 * Numeric counter that animates on scroll-into-view.
 */
function NumericCounter({ target, decimals = 0, suffix = "", duration = 1600 }) {
  const [ref, inView] = useInView({ threshold: 0.35 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  const formatted = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString();
  return (
    <span ref={ref} data-testid="stat-counter" className="tabular-nums">
      {formatted}
      {suffix}
    </span>
  );
}

/**
 * Text-based counter that fades / slides in on scroll-into-view.
 * Used for non-numeric labels like "Hundreds" or "Years".
 */
function TextCounter({ text }) {
  const [ref, inView] = useInView({ threshold: 0.35 });
  return (
    <span
      ref={ref}
      data-testid="stat-text"
      className={`inline-block transition-all duration-1000 ease-out ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
      }`}
    >
      {text}
    </span>
  );
}

export default function CommunityImpact() {
  return (
    <section
      data-testid="community-impact"
      className="relative islamic-pattern-dark text-white py-24 md:py-32 overflow-hidden"
    >
      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="overline text-[color:var(--maqam-gold)]">Community Impact</span>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Numbers that speak for the community.
          </h2>
          <p className="mt-5 text-white/75 leading-relaxed">
            Every count below is a pilgrim, a family, a story of return.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {COMMUNITY_STATS.map((s, i) => (
            <div
              key={i}
              data-testid={`impact-card-${i}`}
              className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 md:p-8 text-center flex flex-col"
            >
              <div className="font-serif leading-none text-[color:var(--maqam-gold)] min-h-[3.5rem] md:min-h-[4rem] flex items-center justify-center">
                {s.isText ? (
                  <span className={`${String(s.value).length > 3 ? "text-4xl md:text-5xl" : "text-5xl md:text-6xl"}`}>
                    <TextCounter text={s.value} />
                  </span>
                ) : (
                  <span className="text-5xl md:text-6xl">
                    <NumericCounter target={s.value} decimals={s.decimals || 0} suffix={s.suffix || ""} />
                  </span>
                )}
              </div>
              <p className="mt-5 font-semibold tracking-wide text-white/95 text-sm md:text-base flex-1">
                {s.label}
              </p>
              {s.note && (
                <p className="mt-2 text-xs text-white/60 leading-relaxed">{s.note}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
